import { useEffect, useMemo, useState } from 'react';
import { localDateTimeToUtcMs, utcMsToLocalInstant } from '@runhuman/shared';
import { buildBreakDecisionModalStyles } from './break-decision-modal-styles';
import {
  clampBreakDuration,
  DURATION_OPTIONS,
  DURATION_STEP_MINUTES,
} from './break-request-duration';
import type { BreakRequestsTheme } from './types';

export interface ComposedBreakWindow {
  readonly startsAt: string;
  readonly endsAt: string;
  readonly rationale: string | undefined;
}

export interface SimpleBreakWindowComposerProps {
  readonly seedStartsAt: string;
  readonly seedEndsAt: string;
  readonly timezone: string;
  readonly busy: boolean;
  readonly theme: BreakRequestsTheme;
  readonly onOffer: (params: ComposedBreakWindow) => void;
  readonly onSchedule: (params: ComposedBreakWindow) => void;
}

interface ComposerState {
  readonly startDate: string;
  readonly startTime: string;
  readonly durationMinutes: number;
}

/**
 * Schedule-free custom-window composer for {@link BreakDecisionModal}. Used by
 * surfaces that lack the tester's merged schedule (the Discord-Activity admin
 * drawer) and so can't constrain the start to shift blocks the way the web
 * admin Counter modal does. It still composes a window as **start + duration**
 * — never a separate end date/time — so a break can never be described as a
 * cross-day span. Bounds come from the shared `break-request-duration`
 * constants, the same source the web pickers read.
 */
export function SimpleBreakWindowComposer({
  seedStartsAt,
  seedEndsAt,
  timezone,
  busy,
  theme,
  onOffer,
  onSchedule,
}: SimpleBreakWindowComposerProps) {
  const styles = useMemo(() => buildBreakDecisionModalStyles(theme), [theme]);
  const initial = useMemo<ComposerState>(() => {
    const start = utcMsToLocalInstant(Date.parse(seedStartsAt), timezone);
    const rawDuration = Math.round((Date.parse(seedEndsAt) - Date.parse(seedStartsAt)) / 60_000);
    return {
      startDate: start.date,
      startTime: toHHMM(start.hour, start.minute),
      durationMinutes: clampBreakDuration(rawDuration),
    };
  }, [seedStartsAt, seedEndsAt, timezone]);

  const [state, setState] = useState<ComposerState>(initial);
  const [rationale, setRationale] = useState('');

  // Re-seed when the parent swaps the request being countered.
  useEffect(() => {
    setState(initial);
    setRationale('');
  }, [initial]);

  const parsed = parseHHMM(state.startTime);
  const trimmedRationale = rationale.trim();

  const submit = (cb: (params: ComposedBreakWindow) => void) => {
    if (!parsed) return;
    const startMs = localDateTimeToUtcMs(state.startDate, parsed.hour, parsed.minute, timezone);
    const endMs = startMs + state.durationMinutes * 60_000;
    cb({
      startsAt: new Date(startMs).toISOString(),
      endsAt: new Date(endMs).toISOString(),
      rationale: trimmedRationale.length > 0 ? trimmedRationale : undefined,
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={styles.composerGrid}>
        <label style={styles.fieldLabel}>
          <span style={styles.fieldCaption}>Start date</span>
          <input
            type="date"
            value={state.startDate}
            onChange={(e) => setState((p) => ({ ...p, startDate: e.target.value }))}
            style={styles.input}
            disabled={busy}
            data-testid="break-decision-modal-start-date"
          />
        </label>
        <label style={styles.fieldLabel}>
          <span style={styles.fieldCaption}>Start time</span>
          <input
            type="time"
            value={state.startTime}
            onChange={(e) => setState((p) => ({ ...p, startTime: e.target.value }))}
            style={styles.input}
            step={DURATION_STEP_MINUTES * 60}
            disabled={busy}
            data-testid="break-decision-modal-start-time"
          />
        </label>
        <label style={styles.fieldLabel}>
          <span style={styles.fieldCaption}>Duration</span>
          <select
            value={state.durationMinutes}
            onChange={(e) => setState((p) => ({ ...p, durationMinutes: Number(e.target.value) }))}
            style={styles.input}
            disabled={busy}
            data-testid="break-decision-modal-duration"
          >
            {DURATION_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d} min
              </option>
            ))}
          </select>
        </label>
      </div>
      <label style={styles.fieldLabel}>
        <span style={styles.fieldCaption}>Rationale (optional)</span>
        <textarea
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          placeholder="e.g. iOS coverage is thin at 2:00 PM — this slot is safer."
          rows={2}
          maxLength={280}
          style={styles.textarea}
          disabled={busy}
        />
      </label>
      <div style={styles.composerActions}>
        <button
          type="button"
          style={styles.secondaryButton}
          disabled={busy || !parsed}
          onClick={() => submit(onOffer)}
          title="Suggest this window — the tester accepts or declines"
        >
          Suggest
        </button>
        <button
          type="button"
          style={styles.primaryButton}
          disabled={busy || !parsed}
          onClick={() => submit(onSchedule)}
          title="Schedule this window now (no tester round)"
        >
          Schedule
        </button>
      </div>
    </div>
  );
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function toHHMM(hour: number, minute: number): string {
  return `${pad2(hour)}:${pad2(minute)}`;
}

function parseHHMM(value: string): { hour: number; minute: number } | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const hour = parseInt(m[1], 10);
  const minute = parseInt(m[2], 10);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}
